'use client';

import type { AccountMultiselectOption as AccountType } from 'src/features/tasks/creation/components/account-multiselect/account-multiselect-types';
import type { FragmentType } from 'src/gql';
import Link from 'next/link';
import { Badge } from '@savvy-kit/components/badge';
import { Button } from '@savvy-kit/components/button';
import { Text } from '@savvy-kit/foundations/text';
import { isNil } from 'es-toolkit';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { CircleDashed } from 'lucide-react';
import { graphql, unmaskFragment } from 'src/gql';

export const StrategyEnrollmentChipFragment = graphql(/* GraphQL */ `
  fragment StrategyEnrollmentChip on Account {
    id
    accountName
    subtype
    mask
    strategyName
    strategyType
    enrollmentStatus
    directIndexingDisplay
    displayBalance {
      amount
    }
    providerAccount {
      providerName
    }
    activeSwimRequest {
      id
      strategyType
      allocationModel
    }
    activeEnrollmentTask {
      salesforceTaskId
    }
    portfolio {
      household {
        salesforceAccountId
        primaryClient {
          salesforceContactId
        }
      }
    }
  }
`);

type StrategyEnrollmentChipComponentProps = {
  readonly accountFrag: FragmentType<typeof StrategyEnrollmentChipFragment>;
};

function StrategyEnrollmentChipComponent({ accountFrag }: StrategyEnrollmentChipComponentProps) {
  const account = unmaskFragment(StrategyEnrollmentChipFragment, accountFrag);

  const isEnrollmentInProgress = account.enrollmentStatus === 'PENDING' || account.enrollmentStatus === 'IN_PROGRESS';
  const salesforceAccountId = account.portfolio?.household.salesforceAccountId;
  const salesforceTaskId = account.activeEnrollmentTask?.salesforceTaskId;
  const canNavigateToTask = isEnrollmentInProgress && !isNil(salesforceAccountId) && !isNil(salesforceTaskId);

  let strategyName = account.strategyName;
  if (isNil(strategyName) && isEnrollmentInProgress) {
    const strategyType = account.activeSwimRequest?.strategyType;
    const allocationModel = account.activeSwimRequest?.allocationModel;
    const parts = [strategyType, allocationModel].filter((part) => !isNil(part) && part !== '');
    strategyName = parts.length > 0 ? parts.join(' ') : 'Strategy';
  }

  if (isNil(account.strategyName) && account.directIndexingDisplay) {
    return <Badge variant="info-muted">Direct Indexing</Badge>;
  }

  if (isNil(account.strategyName) && account.enrollmentStatus === 'NOT_ENROLLED') {
    return <span className="text-sm text-secondary">—</span>;
  }

  const isAssignedStrategy = account.strategyType === 'first_party' || account.strategyType === 'third_party';
  const chipVariant = !isEnrollmentInProgress && isAssignedStrategy ? 'info-muted' : 'secondary-muted';

  const chipElement = (
    <Badge variant={chipVariant}>
      {isEnrollmentInProgress ? (
        <div className="flex flex-row gap-2 items-center overflow-hidden min-w-0 max-w-full">
          <CircleDashed className="size-4 shrink-0 animate-spin" />
          <Text
            render={<span />}
            className="min-w-0 flex-1 truncate"
            style={{ fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit' }}
          >
            {strategyName}
          </Text>
        </div>
      ) : (
        strategyName
      )}
    </Badge>
  );

  return canNavigateToTask ? (
    <Link
      href={{
        pathname: `/advisor/dashboard/client/${salesforceAccountId}`,
        query: {
          taskId: salesforceTaskId,
        },
      }}
    >
      {chipElement}
    </Link>
  ) : (
    chipElement
  );
}

export const StrategyEnrollmentChip = withLDConsumer()(StrategyEnrollmentChipComponent);

type EnrollStrategyButtonProps = {
  readonly accountFrag: FragmentType<typeof StrategyEnrollmentChipFragment>;
  readonly onStartEnhancedEnrollment: (account: AccountType) => void;
};

export function EnrollStrategyButton({ accountFrag, onStartEnhancedEnrollment }: EnrollStrategyButtonProps) {
  const account = unmaskFragment(StrategyEnrollmentChipFragment, accountFrag);

  const isEligible =
    isNil(account.strategyName) &&
    account.enrollmentStatus === 'NOT_ENROLLED' &&
    !account.directIndexingDisplay &&
    !isNil(account.portfolio?.household.salesforceAccountId) &&
    !isNil(account.portfolio.household.primaryClient.salesforceContactId);

  if (!isEligible) return null;

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();

        onStartEnhancedEnrollment({
          id: account.id,
          accountName: account.accountName,
          subtype: account.subtype,
          mask: account.mask,
          providerAccount: account.providerAccount,
          displayBalance: account.displayBalance,
        });
      }}
    >
      Enroll strategy
    </Button>
  );
}
